import * as React from 'react';
import { Input, Label } from 'reactstrap';
import InputDropdown from '../../../widgets/InputDropdown';
import { DARK_PLACEHOLDER, getNewValue, ROUTE_PATH } from '../../../constants';
import { useDispatch, useSelector } from 'react-redux';
import {
  DEFAULT_VIRTUAL_SCANNER,
  onboardingSelector,
} from '../../../reducers/onboarding';
import { OBVirtualizationSoftware, OptionType } from '../../../types';
import { ONBOARDING_ACTIONS } from '../../../actions/onboarding';
import { apiRequest } from '../../../APIClient';
import {
  featureFlagSelector,
  sessionSelector,
} from '../../../reducers/session';
import { ValueType } from 'react-select';

const SOFTWARE_MAP: { [key in OBVirtualizationSoftware]: string } = {
  VMWare: 'VMWare vCenter 6.5 and newer',
  HyperV: 'HyperV 2016 or newer',
  AWS: 'Amazon Web Services',
  Azure: 'Microsoft Azure',
  Other: 'Other',
  None: 'None',
};

const SOFTWARE_OPTIONS = Object.entries(SOFTWARE_MAP)
  .filter(([value]) => value !== OBVirtualizationSoftware.NONE)
  .map(([value, label]) => ({ value, label }));

const OnboardingVirtualScanner = () => {
  const settings = useSelector(onboardingSelector.getVirtualScanner);
  const canEdit = useSelector(sessionSelector.canEditOnboarding);
  const scannerPlatEnabled = useSelector(
    featureFlagSelector.onboarding.scannerPlatformization,
  );
  const orgID = useSelector(sessionSelector.getRiskID);
  const orgIDParam = orgID ? `?id=${orgID}` : '';
  const dispatch = useDispatch();

  const [hasFetchedVirtualScanner, setHasFetchedVirtualScanner] =
    React.useState(settings !== DEFAULT_VIRTUAL_SCANNER);
  const [software, setSoftware] = React.useState<OBVirtualizationSoftware>(
    settings.software,
  );
  const [requirementsMet, setRequirementsMet] = React.useState(
    settings.requirementsMet,
  );
  const [noVirtualScanners, setNoVirtualScanners] = React.useState(
    settings.noVirtualScanners,
  );

  React.useEffect(() => {
    if (!hasFetchedVirtualScanner) {
      dispatch(apiRequest.getOBVirtualScanner());
      setHasFetchedVirtualScanner(true);
    }
  }, [dispatch, hasFetchedVirtualScanner]);

  React.useEffect(() => {
    setRequirementsMet(settings.requirementsMet);
  }, [settings.requirementsMet]);

  React.useEffect(() => {
    setSoftware(settings.software);
  }, [settings.software]);

  React.useEffect(() => {
    if (settings.noVirtualScanners) {
      setSoftware(OBVirtualizationSoftware.NONE);
      setRequirementsMet(false);
    }
    setNoVirtualScanners(settings.noVirtualScanners);
  }, [settings.noVirtualScanners]);

  const toOptionType = React.useMemo(() => {
    if (software === OBVirtualizationSoftware.NONE) {
      return null;
    } else {
      return { value: software, label: SOFTWARE_MAP[software] };
    }
  }, [software]);

  const virtualizationSoftwareChange = React.useCallback(
    (value: ValueType<OptionType>) => {
      const v = getNewValue(value, false);
      const newSoftware = v
        ? (v.value as OBVirtualizationSoftware)
        : OBVirtualizationSoftware.NONE;
      dispatch(
        ONBOARDING_ACTIONS.EDIT_VIRTUAL_SCANNER({
          software: newSoftware,
          requirementsMet,
          noVirtualScanners,
          // Section is considered as complete if a software is selected and the requirements are met
          // Or no virtual scanner is checked
          completed:
            newSoftware !== OBVirtualizationSoftware.NONE && requirementsMet,
        }),
      );
      dispatch(apiRequest.saveOBVirtualScanner());
    },
    [dispatch, noVirtualScanners, requirementsMet],
  );

  const requirementsMetChange = React.useCallback(
    (e) => {
      dispatch(
        ONBOARDING_ACTIONS.EDIT_VIRTUAL_SCANNER({
          software,
          requirementsMet: e.currentTarget.checked,
          noVirtualScanners,
          completed:
            software !== OBVirtualizationSoftware.NONE &&
            e.currentTarget.checked,
        }),
      );
      dispatch(apiRequest.saveOBVirtualScanner());
    },
    [dispatch, noVirtualScanners, software],
  );

  const showDownloads = React.useMemo(
    () => software === OBVirtualizationSoftware.VMWARE && scannerPlatEnabled,
    [software, scannerPlatEnabled],
  );

  return (
    <form className="pl-2">
      <p className="ob-section-description">
        Provide and confirm configuration details for all virtual scanners.
        Scanners perform internal vulnerability scans on your private network
        assets.
      </p>
      <h5 className="pb-1">Virtual Infrastructure Requirements</h5>
      <InputDropdown
        id="virtualSoftware"
        menuPortalTarget={document.body}
        isCreatable={false}
        isMulti={false}
        showDropdownArrow={true}
        onChange={virtualizationSoftwareChange}
        options={SOFTWARE_OPTIONS}
        placeholder="Choose a hypervisor"
        styles={DARK_PLACEHOLDER}
        value={toOptionType}
        isDisabled={noVirtualScanners || !canEdit}
        classNamePrefix="virtualSoftware"
      />
      <h5 className="mt-3 pb-1">Virtual Machine Requirements</h5>
      <div style={{ display: 'grid', paddingTop: '10px' }}>
        <Label className="ob-accordion-checkbox" check>
          <Input
            type="checkbox"
            id="requirementsMet"
            checked={requirementsMet}
            onChange={requirementsMetChange}
            disabled={noVirtualScanners || !canEdit}
          />
          Each virtual scanner meets the following minimum requirements:
        </Label>
        <ul style={{ fontSize: '14px' }}>
          <li>8 vCPUs</li>
          <li>16 GB of RAM</li>
          <li>40 GB of available storage</li>
        </ul>
        {showDownloads && (
          <div className="pt-3">
            Go to{' '}
            <a
              href={ROUTE_PATH.DOWNLOADS.UNIFIED_PORTAL + orgIDParam}
              target="_blank"
              rel="noopener noreferrer"
            >
              Downloads
            </a>{' '}
            to begin downloading and installing virtual scanners.
          </div>
        )}
      </div>
    </form>
  );
};

export const VirtualScannerFooter = () => {
  const dispatch = useDispatch();
  const settings = useSelector(onboardingSelector.getVirtualScanner);
  const { requirementsMet, software, noVirtualScanners } = settings;
  const canEdit = useSelector(sessionSelector.canEditOnboarding);

  const noVirtualScannersChange = React.useCallback(
    (e) => {
      if (e.currentTarget.checked) {
        dispatch(
          ONBOARDING_ACTIONS.EDIT_VIRTUAL_SCANNER({
            software: OBVirtualizationSoftware.NONE,
            requirementsMet: false, // fasle and need to recheck when select software
            noVirtualScanners: e.currentTarget.checked,
            completed: true, // true because no virtual scanner is checked
          }),
        );
      } else {
        dispatch(
          ONBOARDING_ACTIONS.EDIT_VIRTUAL_SCANNER({
            software,
            requirementsMet,
            noVirtualScanners: e.currentTarget.checked,
            completed: false, // false because no virtual scanner is not checked
          }),
        );
      }
      dispatch(apiRequest.saveOBVirtualScanner());
    },
    [dispatch, requirementsMet, software],
  );

  return (
    <Label className="ob-accordion-checkbox" check>
      <Input
        type="checkbox"
        id="noVirtualScanners"
        checked={noVirtualScanners}
        disabled={!canEdit}
        onChange={noVirtualScannersChange}
      />
      I do not need to deploy a vScanner at this time
    </Label>
  );
};

export default OnboardingVirtualScanner;
